/* app-state: bootstrap + SSE (native Last-Event-ID reconnect) + acties. */
import { createContext, useContext, useEffect, useState, useCallback, useRef,
         type ReactNode } from "react";
import { get, mutate, ApiError, outboxFlush } from "./api";
import { Bootstrap as BootstrapSchema } from "@gorodn/contracts";
import { Value } from "@sinclair/typebox/value";

export interface DayView {
  date: string;
  /* Het origineel plus beide talen; de client kiest. Ontbreekt de gevraagde
     taal, dan tonen we het origineel -- een vraag in de taal waarin hij is
     opgeschreven is beter dan een leeg scherm. */
  prompt: { id: string; cat: string | null; original: string;
            textRu?: string | null; textEn?: string | null } | null;
  sealedAt: string | null;
  revealedAt: string | null;
  mine: Record<string, { text: string | null; attachmentId: string | null }>;
  theirsKinds: string[];
  theirs: Record<string, { text: string | null; attachmentId: string | null }> | null;
  photoReactions: { mine: PhotoReactionEmoji | null; theirs: PhotoReactionEmoji | null };
  echoes?: { mine: boolean; theirs: boolean };
  revision: number;
}
export type PhotoReactionEmoji = "❤️" | "🥰" | "😂" | "🥹" | "🔥";
export type MoodKind = "calm" | "focused" | "soft" | "tired" | "low" | "stressed" | "excited" | "playful";
export interface MoodEntry {
  userId: string;
  handle: "R" | "N";
  slotKey: string;
  mood: MoodKind;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface MoodSummary {
  me: MoodEntry | null;
  partner: MoodEntry | null;
  recent: MoodEntry[];
}
export interface CareDay {
  date: string;
  water: number;
  meals: number;
  updatedAt: string | null;
}
export interface CareSummary {
  me: CareDay;
  partner: CareDay;
}
export interface PhotoArtifact {
  date: string;
  state: "solo" | "together";
  contributors: string[];
  museumTarget: string;
}
export interface GardenStats {
  daysBuilt: number;
  lettersDelivered: number;
  capsulesSealed: number;
  capsulesOpened: number;
  songsShared: number;
  sharedSparks?: number;
  gamesFinished?: number;
  gamesOpen?: number;
  chapters: number;
}
export interface LivingSceneBeat {
  type: "mood" | "ritual" | "game" | "garden" | "letter" | "journey";
  time: string | null;
  href: string;
  label: { en: string; ru: string };
}
export interface LivingScene {
  sceneId: string;
  phase: "quiet" | "opening" | "together" | "growth" | "arrival" | "afterglow";
  accent: "gold" | "violet" | "blue" | "green" | "rose";
  headline: { en: string; ru: string };
  subline: { en: string; ru: string };
  primaryAction: { en: string; ru: string; href: string };
  beats: LivingSceneBeat[];
}
export interface BootstrapData {
  /* `place` is de plaats zoals de server hem uit de tijdzone afleidt: de
     client hoeft daar geen tabel voor te kennen. */
  me: { id: string; handle: "R" | "N"; displayName: string; tz: string;
        place: { tz: string; ru: string; en: string } };
  partner: { id: string; handle: "R" | "N"; displayName: string; tz: string;
             place: { tz: string; ru: string; en: string } };
  chapter: { n: number; title: string; state: string;
             meetAt: string | null; tripEnd: string | null;
             tentativeStart: string | null;
             /* Оборот страницы: de wachtstand van het omslaan. */
             nextKeyR: string | null; nextKeyN: string | null;
             nextTitle: string | null };
  /* Waar ze zijn. Alleen `together` tijdens het samen-venster; de plaats komt
     uit het vluchtenbord en kan ontbreken zolang er niets gevlogen is. */
  together: { together: boolean;
              place: { code: string; tz: string; ru: string; en: string } | null };
  today: DayView;
  sharedScene?: LivingScene;
  mood?: MoodSummary;
  care?: CareSummary;
  city: { id: string; date: string | null; kind: string; district: string | null;
          slot: number | null; geometry: Record<string, unknown>;
          /* Na reveal afgeleid uit signalen; oude cached bootstraps mogen dit
             veld missen tot de volgende server-refresh. */
          contributors?: ("R" | "N")[];
          /* Het hoofdstuk waar deze dag bij hoort; null voor landmarks en
             voor dagen zonder rituele dag. */
          chapter: { id: string; n: number; title: string;
                     /* Optioneel voor één oude offline bootstrap; live API
                        levert het hoofdstukbord altijd. */
                     street?: { R: string; N: string } } | null }[];
  post: { traveling: { id: string; author: string; eta: string | null; progress: number }[];
          unopened: number };
  /* Additief en optioneel voor een zachte overgang vanaf oudere, lokaal
     gecachete bootstraps. De live API levert dit altijd; een toestel dat voor
     de update offline start krijgt tijdelijk een lege tuin in plaats van een
     crash. */
  garden?: GardenStats;
  /* Fotodagen als stadsmetadata: begrensd, zonder media-id's; vóór de reveal
     staat alleen de eigen bijdrage van deze lezer erin. Optioneel voor oude
     gecachete bootstraps — dan is de straat gewoon nog even zonder posters. */
  photoArtifacts?: PhotoArtifact[];
  presence: Record<string, boolean>;
  weather: Record<string, unknown> | null;
  /* Publieke VAPID-sleutel; `null` betekent: push staat uit op deze server. */
  pushKey: string | null;
  lastEventSeq: number;
}

export interface WorldEvent { type: string; n: number; by?: string; date?: string; kind?: string }
export interface AppState {
  boot: BootstrapData | null;
  presence: Record<string, boolean>;
  ogonek: number;                       // increments bij ontvangen огонёк
  worldEvent: WorldEvent | null;        // laatste wereld-reactieve gebeurtenis
  cityBuildPending: string | null;      // event binnen, canonieke refresh nog niet rond
  offline: boolean;                     // netwerk weg: gecontroleerde state
  refresh: () => Promise<void>;
  putEntry: (kind: string, body: { text?: string; attachmentId?: string }) => Promise<void>;
  setPhotoReaction: (emoji: PhotoReactionEmoji) => Promise<void>;
  leaveEcho: (date: string) => Promise<"sent" | "pending" | "failed">;
  setMood: (mood: MoodKind, note?: string) => Promise<boolean>;
  changeCare: (kind: "water" | "meal", delta: 1 | -1) => Promise<boolean>;
  sendOgonek: () => Promise<boolean>;
  sendKey: () => Promise<void>;
}
const isRevoked = (e: unknown): boolean =>
  e instanceof ApiError && e.status === 401;

const Ctx = createContext<AppState | null>(null);
export const useApp = () => useContext(Ctx)!;

/* Laatst bekende bootstrap, lokaal op dit apparaat. Zonder deze cache toont
   een koude start zonder netwerk eindeloos het opstartscherm: de sessie is
   geldig, maar er is geen data om de shell mee te vullen.

   Het is de eigen, al geauthenticeerde inhoud van dit apparaat — vergelijkbaar
   met de outbox in IndexedDB. Hij wordt gewist zodra de server de sessie
   afwijst. */
const BOOT_CACHE = "gorodn_boot_cache";
const readBootCache = (): BootstrapData | null => {
  try {
    const raw = localStorage.getItem(BOOT_CACHE);
    return raw ? (JSON.parse(raw) as BootstrapData) : null;
  } catch { return null; }
};
const writeBootCache = (b: BootstrapData | null) => {
  /* Aanwezigheid hoort niet in een cache die een herstart overleeft. */
  if (b) b = { ...b, presence: {} };
  try {
    if (b) localStorage.setItem(BOOT_CACHE, JSON.stringify(b));
    else localStorage.removeItem(BOOT_CACHE);
  } catch { /* vol of privémodus: dan zonder cache */ }
};
export const clearBootCache = () => writeBootCache(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<BootstrapData | null>(null);
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  const [ogonek, setOgonek] = useState(0);
  const [worldEvent, setWorldEvent] = useState<WorldEvent | null>(null);
  const [offline, setOffline] = useState(false);
  const [cityBuildPending, setCityBuildPending] = useState<string | null>(null);
  const evn = useRef(0);

  /* Zeggen waar dit toestel is. De browser kent zijn eigen tijdzone; die
     verspringt vanzelf zodra je landt, dus niemand hoeft iets bij te houden.
     Eén keer per sessie, en stil falen: de kop klopt dan gewoon nog even
     niet -- dat is geen reden om de app te laten struikelen. */
  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    void mutate("PUT", "/me/place", { tz }).catch(() => { /* niet erg */ });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const raw = await get<unknown>("/bootstrap");
      if (!Value.Check(BootstrapSchema, raw)) {
        throw new ApiError(502, "City data is temporarily unavailable",
          "The app received an unexpected server response.");
      }
      const b = raw as BootstrapData;
      setBoot(b); setPresence(b.presence); setOffline(false);
      setCityBuildPending(pending => pending && b.city.some(c => c.date === pending)
        ? null : pending);
      writeBootCache(b);
    } catch (e) {
      if (e instanceof ApiError) {
        /* Sessie afgewezen: lokale kopie weg én de app moet afsluiten. Alleen
           de cache wissen liet het private scherm gemount staan met oude
           inhoud. */
        if (e.status === 401) {
          /* De intrekking zelf wordt in api.ts gemeld, voor élke aanroep.
             Hier alleen nog: dit is afgehandeld, dus niet doorgooien -- dat
             zou een onafgevangen rejection opleveren voor iets wat we juist
             netjes opvangen. Een 403 valt hier bewust NIET onder. */
          clearBootCache();
          return;
        }
        throw e;                           // echte serverfout blijft zichtbaar
      }
      setOffline(true);                    // netwerk weg — outbox vangt mutaties
      /* Val terug op de laatst bekende stand, zodat een koude start zonder
         netwerk de private shell toont in plaats van eindeloos «ГОРОД N…». */
      /* Aanwezigheid is vluchtig: wie er nú is, weet je alleen via de live
         verbinding. Uit cache herstellen zou offline «jullie zijn er allebei»
         kunnen beweren en zo Same Sky openen. De rest van de laatst bekende
         stand mag wél terug. */
      setBoot(prev => {
        if (prev) return prev;
        const cached = readBootCache();
        if (cached) setPresence({});
        return cached;
      });
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /* netwerkfout-grens: een verwachte offline-fetchfout is een state, geen
     pageerror — al het andere blijft gewoon hard falen */
  useEffect(() => {
    const onRej = (e: PromiseRejectionEvent) => {
      /* Een ingetrokken sessie is afgehandeld: api.ts heeft de intrekking al
         gemeld en de app sluit af. Die rejection hoeft dus geen runtimefout
         te worden. Alle ándere serverfouten blijven wél hard zichtbaar. */
      if (e.reason instanceof ApiError && e.reason.status === 401) {
        e.preventDefault();
        return;
      }
      const m = e.reason instanceof TypeError ? String(e.reason.message) : "";
      if (/fetch|load failed|network/i.test(m)) { e.preventDefault(); setOffline(true); }
    };
    /* Aanwezigheid is alleen waar zolang de verbinding staat. Valt die weg,
       dan weten we niets meer -- en mag Same Sky dus niet meer open. */
    const onOff = () => { setOffline(true); setPresence({}); };
    const onOn = () => { setOffline(false); void refresh(); void outboxFlush(); };
    addEventListener("unhandledrejection", onRej);
    addEventListener("offline", onOff);
    addEventListener("online", onOn);
    return () => {
      removeEventListener("unhandledrejection", onRej);
      removeEventListener("offline", onOff);
      removeEventListener("online", onOn);
    };
  }, [refresh]);

  useEffect(() => {
    if (!boot) return;
    const es = new EventSource("/api/v1/events");
    let lastActivity = Date.now();
    const markLive = () => { lastActivity = Date.now(); setOffline(false); };
    const markDisconnected = () => {
      setOffline(true);
      setPresence({});
    };
    const onDomain = (e?: Event) => {
      markLive();
      const me = e as MessageEvent | undefined;
      if (me?.type) {
        let data: Record<string, unknown> = {};
        try { data = JSON.parse(me.data) as Record<string, unknown>; }
        catch { /* geen data */ }
        const by = typeof data.by === "string" ? data.by : undefined;
        const we: WorldEvent = { type: me.type, n: ++evn.current };
        if (by !== undefined) we.by = by;
        if (typeof data.date === "string") we.date = data.date;
        if (typeof data.kind === "string") we.kind = data.kind;
        if (me.type === "city.built.v1" && typeof data.date === "string") {
          setCityBuildPending(data.date);
          /* Optionele, korte bevestiging; browsers zonder vibratie blijven
             volledig stil. Geluid is bewust geen onderdeel van deze reveal. */
          try { if (document.visibilityState === "visible") navigator.vibrate?.([12, 35, 12]); }
          catch { /* haptiek is decoratief */ }
        }
        setWorldEvent(we);
      }
      /* Een domeinevent mag nooit een pageerror veroorzaken als de daarop
         volgende snapshot nét tijdens een deploy faalt. Het event zelf blijft
         zichtbaar; de volgende heartbeat/actie haalt opnieuw op. */
      void refresh().catch(() => { /* bestaande staat blijft bruikbaar */ });
    };
    for (const t of ["signal.recorded.v1", "ritual.revealed.v1", "ritual.sealed.v1",
                     "city.built.v1", "letter.sent.v1", "letter.delivered.v1",
                     "letter.opened.v1", "chapter.transitioned.v1", "chapter.trip.set.v1",
                     "capsule.sealed.v1", "capsule.opened.v1",
                     "quest.proposed.v1", "quest.chosen.v1", "quest.completed.v1",
                     "recap.published.v1", "weather.updated.v1", "mood.updated.v1",
                     /* Verhuist er iemand, dan hoort de kop van de ander
                        meteen te kloppen -- niet pas bij zijn volgende start. */
                     "place.moved.v1", "care.updated.v1", "home.updated.v1", "garden.updated.v1", "city-history.updated.v1",
                     "planner.command.applied.v1", "photo.reacted.v1", "echo.left.v1"])
      es.addEventListener(t, onDomain);
    es.addEventListener("ogonek.v1", (e) => {
      const d = JSON.parse((e as MessageEvent).data);
      if (d.from !== boot.me.handle) setOgonek(n => n + 1);
      setWorldEvent({ type: "ogonek.v1", n: ++evn.current, by: d.from });
    });
    for (const t of ["radio.track.added.v1", "radio.track.deleted.v1",
                     "radio.broadcast.published.v1", "game.started.v1", "game.moved.v1",
                     "game.finished.v1", "game.abandoned.v1", "same-street.spark.v1"])
      es.addEventListener(t, onDomain);
    es.addEventListener("presence", (e) => {
      markLive();
      setPresence(JSON.parse((e as MessageEvent).data));
    });
    es.addEventListener("heartbeat", markLive);
    es.onopen = markLive;
    es.onerror = () => {
      /* SSE weg: we weten niet meer wie er is. Meteen leegmaken, niet wachten
         op een timeout of op de volgende bootstrap. */
      markDisconnected();
    };
    /* iOS kan een halfopen SSE-socket minutenlang als open beschouwen. De
       server praat iedere vijf seconden; na twee gemiste slagen is presence
       niet langer een feit. navigator.onLine is slechts een snelle extra
       aanwijzing, nooit de enige bron. */
    const watchdog = window.setInterval(() => {
      if (!navigator.onLine || Date.now() - lastActivity > 11_000)
        markDisconnected();
    }, 1000);
    return () => {
      window.clearInterval(watchdog);
      setPresence({});
      es.close();
    };
  }, [boot?.me.handle, refresh]);

  const putEntry = useCallback(async (kind: string,
      body: { text?: string; attachmentId?: string }) => {
    if (!boot) return;
    const opts = body.attachmentId?.startsWith("tmp:")
      ? { offline: { deps: [body.attachmentId.slice(4)] } } : {};
    const result = await mutate<{ ok: boolean }>("PUT",
      `/ritual-days/${boot.today.date}/entries/${kind}`, body, opts);
    if ("queued" in result) {
      /* Offline bewaren moet ook direct zichtbaar zijn. Zonder deze lokale
         projectie leek Today leeg en kon dezelfde bijdrage opnieuw worden
         ingestuurd terwijl hij al veilig in de outbox stond. */
      setBoot(prev => {
        if (!prev) return prev;
        const next = { ...prev, today: { ...prev.today,
          mine: { ...prev.today.mine, [kind]: {
            text: body.text ?? null, attachmentId: body.attachmentId ?? null,
          } } } };
        writeBootCache(next); return next;
      });
      return;
    }
    await refresh();
  }, [boot, refresh]);

  /* Огонёк is een live tik, geen bericht. Offline uren later alsnog sturen
     is misleidend; elke fout wordt daarom een expliciete `false` voor de UI
     en nooit een onafgevangen promise of outbox-job. */
  const sendOgonek = useCallback(async () => {
    try {
      const result = await mutate<{ ok: boolean }>("POST", "/ogonek", {},
        { queueOnNetworkError: false });
      return !("queued" in result);
    } catch { return false; }
  }, []);

  const setPhotoReaction = useCallback(async (emoji: PhotoReactionEmoji) => {
    if (!boot || boot.today.photoReactions?.mine) return;
    const result = await mutate<{ ok: boolean; day: DayView }>("PUT",
      `/ritual-days/${boot.today.date}/photo-reaction`, { emoji });
    if ("queued" in result) {
      setBoot(prev => {
        if (!prev) return prev;
        const next = { ...prev, today: { ...prev.today,
          photoReactions: { ...prev.today.photoReactions, mine: emoji } } };
        writeBootCache(next); return next;
      });
      return;
    }
    /* Het mutatieantwoord bevat de verse dag. Die direct projecteren voorkomt
       dat een gelijktijdige SSE-refresh op tragere WebKit-toestellen de net
       gekozen sticker nog één snapshot lang laat verdwijnen. */
    setBoot(prev => {
      if (!prev) return prev;
      const next = { ...prev, today: result.day };
      writeBootCache(next); return next;
    });
  }, [boot]);
  const leaveEcho = useCallback(async (date: string) => {
    try {
      const result = await mutate<{ ok: boolean; day: DayView }>("POST",
        `/ritual-days/${date}/echo`, {});
      if ("queued" in result) return "pending" as const;
      setBoot(prev => {
        if (!prev || prev.today.date !== date) return prev;
        const next = { ...prev, today: result.day };
        writeBootCache(next); return next;
      });
      return "sent" as const;
    } catch { return "failed" as const; }
  }, []);
  const setMood = useCallback(async (mood: MoodKind, note?: string) => {
    if (!boot) return false;
    try {
      const result = await mutate<{ ok: boolean }>("PUT", "/moods/current",
        { mood, note: note?.trim() || undefined }, { queueOnNetworkError: false });
      if ("queued" in result) return false;
      await refresh();
      return true;
    } catch {
      return false;
    }
  }, [boot, refresh]);

  const changeCare = useCallback(async (kind: "water" | "meal", delta: 1 | -1) => {
    if (!boot) return false;
    try {
      const method = delta > 0 ? "POST" : "DELETE";
      const result = await mutate<{ ok: boolean; care: CareDay }>(
        method, `/care/today/${kind}`, {}, { queueOnNetworkError: true });
      if ("queued" in result) {
        setBoot(prev => {
          if (!prev) return prev;
          const current = prev.care ?? {
            me: { date: prev.today.date, water: 0, meals: 0, updatedAt: null },
            partner: { date: prev.today.date, water: 0, meals: 0, updatedAt: null },
          };
          const field = kind === "water" ? "water" : "meals";
          const cap = kind === "water" ? 99 : 20;
          const next = { ...current.me,
            [field]: Math.max(0, Math.min(cap, current.me[field] + delta)),
            updatedAt: new Date().toISOString() };
          const updated = { ...prev, care: { ...current, me: next } };
          writeBootCache(updated); return updated;
        });
        return true;
      }
      setBoot(prev => {
        if (!prev) return prev;
        const current = prev.care ?? {
          me: result.care,
          partner: { date: result.care.date, water: 0, meals: 0, updatedAt: null },
        };
        const updated = { ...prev, care: { ...current, me: result.care } };
        writeBootCache(updated); return updated;
      });
      return true;
    } catch { return false; }
  }, [boot]);

  const sendKey = useCallback(async () => {
    try { await mutate("POST", "/chapter/key", {}); }
    catch (e) { if (!isRevoked(e)) throw e; return; }
    await refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ boot, presence, ogonek, worldEvent, cityBuildPending, offline,
    refresh, putEntry, setPhotoReaction, leaveEcho, setMood,
    changeCare, sendOgonek, sendKey }}>
    {children}</Ctx.Provider>;
}
